import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MarkdownLite } from './Plan';
import { fetchAgreements } from '../data/legal';
import PageWorld from '../components/site/PageWorld';
import { PHYSICAL, SECURITY } from '../data/worlds';
import { FactChips, CapabilityGrid, Faq } from '../components/module';
import { WORKS_WITH_GROUPS } from '../data/worksWith';

// `world` wraps the page in its 3D world (data/worlds); the legal pages
// pass none and stay plain documents.
function Frame({ title, lede, children, world = null }) {
  return (
    <PageWorld world={world}>
      <div className="cs-store">
        <header className="cs-store-hero">
          <h1>{title}</h1>
          <p style={{ maxWidth: 680 }}>{lede}</p>
        </header>
        <section className="cs-store-section" style={{ maxWidth: 720 }}>
          {children}
        </section>
      </div>
    </PageWorld>
  );
}

export function Physical() {
  return (
    <Frame
      world={PHYSICAL}
      title="GridOS"
      lede="The control plane for warehouses, fleets, and operators. It is a separate product from Platform and App Kit."
    >
      <p>
        Safety systems stay in charge. Local controllers are not overridden
        from the network. GridOS holds the live state and the actions that
        policy allows. A person takes the exception when confidence drops.
      </p>
      <p>
        Platform writes into systems you already use. App Kit is the app
        people open. GridOS coordinates physical operations. Prices for that
        work live on its own site, not on{' '}
        <Link to="/pricing">nventr.io pricing</Link>.
      </p>
      <div className="cs-land-cta-row">
        <a
          className="cs-btn cs-btn-primary"
          href="https://gridos.nventr.ai/"
          target="_blank"
          rel="noreferrer"
        >
          Open GridOS
        </a>
        <Link to="/pricing" className="cs-btn cs-btn-outline">
          Platform and App Kit pricing
        </Link>
      </div>
    </Frame>
  );
}

function LegalDoc({ kind, fallbackTitle }) {
  const [doc, setDoc] = useState(undefined);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    fetchAgreements()
      .then((list) => { if (alive) setDoc(list.find((d) => d.kind === kind) || null); })
      .catch((err) => { if (alive) { setDoc(null); setError(err.message); } });
    return () => { alive = false; };
  }, [kind]);
  return (
    <Frame title={doc?.title || fallbackTitle} lede="Nventr AI, LLC. The version you agree to is the one recorded with your acceptance.">
      {doc === undefined && <p className="cs-help">Loading…</p>}
      {doc === null && <p className="cs-help">{error || 'This document is not available right now.'}</p>}
      {doc && <MarkdownLite text={doc.body} />}
      <p className="cs-help" style={{ marginTop: 24 }}>
        <Link to="/terms">Terms</Link>
        {' · '}
        <Link to="/privacy">Privacy</Link>
        {' · '}
        <Link to="/eula">End user agreement</Link>
      </p>
    </Frame>
  );
}

export function Privacy() {
  return <LegalDoc kind="privacy" fallbackTitle="Privacy" />;
}

export function Terms() {
  return <LegalDoc kind="tos" fallbackTitle="Terms" />;
}

export function Eula() {
  return <LegalDoc kind="eula" fallbackTitle="End user agreement" />;
}

// /security — the boundary, then what an enterprise buyer asks next:
// identity, audit, policy, secrets, tenancy, what leaves an instance.
// Product facts stated plainly; no certification is claimed.
const SECURITY_FACTS = [
  { v: '0', k: 'model writes, structurally' },
  { v: '2', k: 'gate kinds: a person, a keyword filter' },
  { v: 'SAML · OIDC', k: 'SSO from Business · SCIM 2.0 on Enterprise' },
  { v: 'counts', k: 'what phones home — never content' },
];
const SECURITY_CAPS = [
  {
    group: 'The write', id: 'sec-write', intro: 'How a side effect is allowed to happen.',
    items: [
      { name: 'A gate on every path', body: 'Before a run, every path from a model step to a write must pass a person or a keyword filter. An LLM yes or no is not a gate. A second ungated branch fails the graph.', view: 'nQube · agentOS' },
      { name: 'Undeclared means impossible', body: 'A side effect is a capability declared on the pipeline — a prefix, a dataset, an audience, an https endpoint, an image prefix. The model holds no write-capable tool.', view: 'agentOS' },
      { name: 'A receipt per commit', body: 'Runtime identity, pipeline, block, capability, the review that allowed it, payload hash, trace id, idempotency key, policy version.', view: 'commit record' },
    ],
  },
  {
    group: 'Identity', id: 'sec-identity', intro: 'Who may sign in, and how.',
    items: [
      { name: 'SSO: SAML and OIDC', body: 'Per-organization provider configuration from Platform Business; “Continue with SSO” on the login page discovers it from the email domain. An organization can enforce SSO so a non-SSO session has no role.', view: 'Administration · SSO', plan: 'Business' },
      { name: 'SCIM 2.0', body: 'Users and Groups endpoints per organization with a bearer token, creating members and access grants from your directory. Enterprise.', view: 'Administration · SSO', plan: 'Enterprise' },
      { name: 'Roles and permissions', body: 'viewer < member < admin < owner, enforced by the data rules; a permissions table sets the minimum role per action and applies without a deploy.', view: 'Administration · Permissions' },
      { name: 'On an App Kit instance', body: 'SAML on the instance comes with Kit Business; the instance verifies tokens from its own auth and never calls a vendor SDK directly.', view: 'instance' },
    ],
  },
  {
    group: 'Record', id: 'sec-record', intro: 'What is written down, and who can change the rules.',
    items: [
      { name: 'Audit trail', body: 'Written server-side for console actions, secret changes, reviews, releases and remediations. Exportable as CSV or NDJSON by range, with a nightly archive.', view: 'Administration · Audit' },
      { name: 'Policy history', body: 'Guardrails, review routing, permissions, environment controls, cost limits and the model ladder are numbered revisions — who, when, before, after — with restore. The client cannot write them directly.', view: 'Administration · Policy history' },
      { name: 'Environment controls', body: 'The minimum role that may release to, or run in, sandbox, UAT and production, enforced on the deploy and on the run.', view: 'Administration · Environment controls' },
    ],
  },
  {
    group: 'Secrets', id: 'sec-secrets', intro: 'Where credentials live, and where they never go.',
    items: [
      { name: 'Runtime-held', body: 'Secrets resolve on the server at run time from the secret store. They are not in the page, not in git, not in the model context.', view: 'runtime' },
      { name: 'The Vault', body: 'Your own keys, certificates and keystores stored once at the organization, sealed, shown as name, kind and fingerprint, placed on hosted instances as file secrets and re-placed on every re-stamp and rotation.', view: 'Administration · Vault' },
      { name: 'Integration secrets', body: 'Stored once under a per-organization name; the value is never logged or displayed again; an audit row records the change by name only.', view: 'Integrations' },
    ],
  },
  {
    group: 'Tenancy and data', id: 'sec-tenancy', intro: 'Who can see what.',
    items: [
      { name: 'Organization → workspace → project', body: 'Every domain record carries its tenant fields and the rules read them directly. Workspaces segregate inside an organization.', view: 'rules' },
      { name: 'Knowledge gated per chunk', body: 'Audience is on every chunk and checked in code against the verified caller; the client cannot read the corpus at all.', view: 'Knowledge' },
      { name: 'A dedicated instance', body: 'App Kit is your URL, database and layer repo on their own stack; the kit you rent is read-only and cannot commit into your repo.', view: 'App Kit' },
    ],
  },
  {
    group: 'What leaves an instance', id: 'sec-phone-home', intro: 'The two channels, stated plainly.',
    items: [
      { name: 'Usage counters, hourly', body: 'Signed counts — runs, hours, storage — with an HMAC signature. Documents, files and prompts do not leave. Health every five minutes; storage sampled daily.', view: 'instance' },
      { name: 'License check, every 12 hours', body: 'The instance confirms it is still entitled. A contracted perpetual license turns both channels off.', view: 'instance' },
      { name: 'Assurance scans', body: 'A monthly passive baseline on the public surface — spider, passive rules, template checks, TLS — on the Assurance add-on. A deeper active test is a confirmed engagement, never a timer.', view: 'fleet' },
    ],
  },
];
const SECURITY_FAQ = [
  { q: 'Which certifications do you hold?', a: 'This page does not claim one. It states the mechanisms; ask us for the current status on the contact form.' },
  { q: 'Can the model be prompted into calling a new system?', a: 'No. A call to anything not declared on the pipeline does not happen, and the model never holds the credential for the ones that are.' },
  { q: 'Where is SSO available?', a: 'SAML and OIDC from Platform Business; SCIM provisioning is Enterprise. On App Kit, SAML on the instance comes with Kit Business.' },
  { q: 'Can I run it where you cannot see the data?', a: 'Yes. The same instance runs in your cloud or on-prem; the license stays, the host meters drop, and only the counters and the license check phone home. A contracted perpetual license turns those off too.' },
];

export function Security() {
  return (
    <PageWorld world={SECURITY}>
      <div className="cs-store cs-land" data-nova-note="The Security page: the write boundary, then identity (SSO, SCIM, roles), audit and policy history, secrets and the Vault, tenancy, and what leaves an instance.">
        <header className="cs-store-hero">
          <h1>Security</h1>
          <p style={{ maxWidth: 680 }}>How a write is allowed to happen, who may sign in, what is written down, where secrets live, and what leaves an instance.</p>
          <FactChips facts={SECURITY_FACTS} />
        </header>
        <section className="cs-store-section" id="boundary">
          <h2>The boundary</h2>
          <p className="cs-mod-prose">
            The model proposes. A gate decides. The gate is a person, or a
            keyword filter. An LLM yes or no is not a gate. Only the runtime
            commits, and the commit record names that runtime. A side effect
            has to be a capability you declared; undeclared means the call
            does not happen, and the credential for the ones you did declare
            stays with the runtime, never in the model context.
          </p>
        </section>
        <CapabilityGrid groups={SECURITY_CAPS} title="What that means in practice" />
        <Faq items={SECURITY_FAQ} />
        <section className="cs-store-section">
          <p className="cs-mod-prose">
            Report a problem on the <Link to="/contact">contact form</Link>.
          </p>
        </section>
      </div>
    </PageWorld>
  );
}

// /works-with — the conventional integrations page, from one record
// (data/worksWith.js) the homepage rows also read. Entries that are not
// supported today carry a status and are labelled, never implied.
const WORKS_WITH_FACTS = [
  { v: '6', k: 'knowledge connectors' },
  { v: '5', k: 'auth kinds on any REST API' },
  { v: '12', k: 'model providers, or your own endpoint' },
  { v: 'SAML · OIDC · SCIM', k: 'identity' },
];
const WORKS_WITH_GRID = WORKS_WITH_GROUPS.map((g) => ({
  group: g.group, id: g.id, intro: g.intro,
  items: g.items.map((it) => ({ name: it.name, body: it.body, view: it.via ? `via ${it.via}` : undefined, plan: it.tag })),
}));
const WORKS_WITH_FAQ = [
  { q: 'Why is there no logo wall?', a: 'Because the honest unit is the mechanism, not the brand. A system you reach through a declared REST call works whether or not we have drawn its logo; one we cannot reach today is listed as “ask us”, not hidden.' },
  { q: 'Is Azure supported?', a: 'Yes. An App Kit instance runs on native Azure — Container Apps, Blob Storage, Key Vault, Container Registry, Log Analytics, Azure Database for PostgreSQL and Communication Services Email, selected by one switch (CLOUD_PROVIDER=azure) — or as sealed images on a VM in your subscription, and the plane hosts instances on Azure VMs. Microsoft identity (Entra ID), SharePoint, Teams and Microsoft 365 mail work on either cloud. nventr.io itself runs on AWS.' },
  { q: 'What about Dropbox, OneDrive or Box?', a: 'Dropbox and Google Drive are knowledge connectors: a folder, synced on a schedule. OneDrive and Box are not today — upload the files, point a crawl at a shared site, or tell us which one you need first on the contact form.' },
  { q: 'Do you have a native connector for my ERP or CRM?', a: 'A declared REST or JSON API with the auth it needs. That is the same path for NetSuite, Salesforce, HubSpot, Zendesk, Jira or QuickBooks; the credential stays in the secret store and the call is a capability you declared, so an undeclared write cannot happen.' },
  { q: 'Can I run entirely on my own hardware?', a: 'Yes: the one-VM self-host stamp with Supabase and MinIO, models on vLLM or Ollama, Gitea for git, and the sovereign runner for Dev Console. The license moves with the server.' },
];

export function WorksWith() {
  return (
    <PageWorld world={SECURITY}>
      <div className="cs-store cs-land" data-nova-note="The Works with page: what nventr connects to — clouds and where it runs, identity providers, knowledge sources, systems a workflow writes into, model providers, code and delivery. Entries marked in progress or ask us are not supported today.">
        <header className="cs-store-hero">
          <h1>Works with</h1>
          <p style={{ maxWidth: 680 }}>What nventr connects to, by mechanism. Everything marked today is in the product now; anything else says so.</p>
          <FactChips facts={WORKS_WITH_FACTS} />
        </header>
        <CapabilityGrid groups={WORKS_WITH_GRID} title="By category" />
        <Faq items={WORKS_WITH_FAQ} />
        <section className="cs-store-section">
          <p className="cs-mod-prose">
            Need one that is not here? Say which on the <Link to="/contact">contact form</Link>;
            how a declared system is called is on <Link to="/platform/integrations">Integrations</Link>,
            and where a workflow may write is on <Link to="/security">Security</Link>.
          </p>
        </section>
      </div>
    </PageWorld>
  );
}
